import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { adminApi } from "../../../../../../../services/Api/Admin/adminApi";
import { TransactionApi } from "../../../../../../../services/Api/Admin/adminTypes";

interface Props {
	transaction?: TransactionApi;
}

const TransactionForm = ({ transaction }: Props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const form = useForm<TransactionApi>({
		criteriaMode: "all",
		defaultValues: {
			...transaction,
			organization_id: transaction?.organization?.id,
		},
	});
	const {
		handleSubmit,
		formState,
		formState: { errors },
		setError,
		control,
		register,
	} = form;
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation("admin");
	const history = useHistory();

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			let res: TransactionApi;
			if (transaction?.id) {
				res = await adminApi.updateTransaction(transaction.id, data);
			} else {
				res = await adminApi.createTransaction(data);
			}
			history.push(`/admin/transactions/${res.id}`);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const searchOrganizations = async (search: any, params: Record<string, any>, options?: Record<string, any>) => {
		const res = await adminApi.getListAdminOrganizationsListSearchSelect(search, params, undefined, {
			cancelToken: options?.token,
		});
		return res.map((organization) => {
			return {
				id: organization.id,
				name: organization.label,
			};
		});
	};

	return (
		<FormDirty formState={formState} onSubmit={onSubmit} key="transaction-form" loading={loading}>
			<div className="row">
				<div className={"col-md-6"}>
					<FormInput name="name" errors={errors} register={register} label={t("lib:common.word.name")} />
				</div>
				<div className={"col-md-6"}>
					<FormSelectGroup
						name="organization_id"
						errors={errors}
						control={control}
						loadOptions={searchOrganizations}
						getOptionLabel={(option) => option.name}
						getOptionValue={(option) => option.id}
						defaultValue={{
							id: transaction?.organization?.id,
							name: transaction?.organization?.name,
						}}
						label={t("lib:common.word.location")}
						data-testid="organization_id"
					/>
				</div>
			</div>
			<div className={"row"}>
				<div className={"col-md-6"}>
					<FormNumberInput
						name="quantity"
						errors={errors}
						control={control}
						suffix=""
						label={t("common.word.amount")}
					/>
				</div>
				<div className={"col-md-6"}>
					<FormMoneyInput
						name="unit_price.amount"
						errors={errors}
						control={control}
						currency="PLN"
						label={t("modules.transaction.field.unit_price.title")}
					/>
				</div>
			</div>
			<div className={"row"}>
				<div className={"col-md-6"}>
					<FormInput
						as="textarea"
						rows={3}
						label={t("lib:common.word.description")}
						register={register}
						name="description"
						errors={errors}
						style={{ minHeight: "calc(2 * 1px + 2 * 6px + 50.391px)" }}
					/>
				</div>
			</div>
		</FormDirty>
	);
};

export default TransactionForm;
